import { useQuery } from "@apollo/client";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import GetGatheringAndUsersForFilterModalDocument from "src/api/graphql/__generated__/documents/GetGatheringAndUsersForFilterModalDocument";
import GatheringUserVariant from "src/api/graphql/__generated__/enums/GatheringUserVariant";
import log from "src/helpers/log";
import useMountEffect from "src/hooks/useMountEffect";
import useStateRef from "src/hooks/useStateRef";
import GatheringFilterSortContext from "../contexts/GatheringFilterSortContext";
import useGatheringFilterSortStore from "./useGatheringFilterSortStore";

const BASE_FETCH_AMOUNT = 32;
const useGatheringUsersForFilter = (
	gatheringId: string,
	{ search, variant }: { search?: string; variant?: GatheringUserVariant } = {},
) => {
	const [loading, setLoading, loadingRef] = useStateRef(false);
	const [refreshing, setRefreshing, refreshingRef] = useStateRef(false);
	const currentScreen = useGatheringFilterSortStore((state) => state.currentScreen);

	const { data, previousData, fetchMore, refetch } = useQuery(
		GetGatheringAndUsersForFilterModalDocument,
		{
			fetchPolicy: "cache-first",
			variables: {
				input: { gatheringId },
				usersInput: { limit: BASE_FETCH_AMOUNT, search, variant },
			},
		},
	);

	const ctx = useContext(GatheringFilterSortContext);

	const gathering = useMemo(() => {
		currentScreen;
		const gathering = (data ?? previousData)?.getGathering;
		if (!gathering?.users?.payload || !ctx) return gathering;

		// Selected users are first
		const filterArr =
			variant === GatheringUserVariant.Uploader
				? ctx?.store.getState().filterUploaderIds
				: ctx?.store.getState().filterUserIds;
		const filterUserIdsSet = new Set(filterArr);
		const sortedUsers = [];
		for (const userId of filterArr) {
			const user = gathering.users.payload.find((u) => u.userId === userId);
			if (!user) {
				log.warn({ userId }, "User not found in gathering");
				continue;
			}
			sortedUsers.push(user);
		}
		// Then the rest of users in the same order as returned by server
		for (const user of gathering.users.payload) {
			if (!filterUserIdsSet.has(user.userId)) {
				sortedUsers.push(user);
			}
		}
		return {
			...gathering,
			users: {
				...gathering.users,
				payload: sortedUsers,
			},
		};
	}, [ctx, data, previousData, currentScreen, variant]);

	const cursor = useRef<string | null>(null);
	useEffect(() => {
		if (data?.getGathering?.users?.cursor) {
			cursor.current = data.getGathering.users.cursor;
		}
	}, [data?.getGathering?.users?.cursor]);

	const handleFetchMore = useCallback(async () => {
		if (loadingRef.current) return;
		if (refreshingRef.current) return;
		if (cursor.current === "{}") return;
		if (search) return;
		setLoading(true);
		try {
			const { data } = await fetchMore({
				variables: {
					input: {
						gatheringId,
					},
					usersInput: {
						limit: BASE_FETCH_AMOUNT,
						cursor: cursor.current,
					},
				},
			});
			cursor.current = data?.getGathering?.users?.cursor ?? null;
		} catch (err) {
			log.error({ err }, "Error fetching more users");
		} finally {
			setLoading(false);
		}
	}, [fetchMore, gatheringId, loadingRef, refreshingRef, search, setLoading]);

	const handleRefresh = useCallback(async () => {
		try {
			setRefreshing(true);
			const { data } = await refetch({
				input: {
					gatheringId,
				},
				usersInput: {
					limit: BASE_FETCH_AMOUNT,
					search,
					variant,
				},
			});
			cursor.current = data?.getGathering?.users?.cursor ?? null;
		} catch (err) {
			log.error({ err }, "Error refreshing gathering and users");
		} finally {
			setRefreshing(false);
		}
	}, [setRefreshing, refetch, gatheringId, search, variant]);

	useMountEffect(() => {
		handleRefresh();
	}, []);

	return {
		gathering,
		loading,
		refreshing,
		fetchMore: handleFetchMore,
		refresh: handleRefresh,
	};
};

export type UseGatheringAndUsersForFilterModal_Gathering = NonNullable<
	ReturnType<typeof useGatheringUsersForFilter>["gathering"]
>;
export type UseGatheringAndUsersForFilterModal_User = NonNullable<
	NonNullable<
		NonNullable<ReturnType<typeof useGatheringUsersForFilter>["gathering"]>["users"]
	>["payload"]
>[number];

export default useGatheringUsersForFilter;
